import React, { useState, useCallback, useRef, useContext, useEffect, lazy, Suspense } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import '../components/Cabinet/CabinetPage.css';
import Sidebar from '../components/Cabinet/Sidebar';
import { SearchProvider, SearchContext } from '../context/SearchContext';
import SearchHeader from '../components/common/SearchHeader';
import Mark from 'mark.js';
import generalIcon from "../assets/general.png";
import generalIcon1 from "../assets/general1.png";
import chatsIcon from '../assets/chat.png';
import chatsIcon1 from '../assets/chat1.png';
import leadsIcon from '../assets/lead.png';
import leadsIcon1 from '../assets/lead1.png';
import settingsIcon from '../assets/settings.png';
import settingsIcon1 from '../assets/settings1.png';
import subscribeIcon from '../assets/subscribe.png';
import subscribeIcon1 from '../assets/subscribe1.png';
import kbIcon from '../assets/brain.png';
import kbIcon1 from '../assets/brain1.png';
import aiIcon from '../assets/ai.png';
import ai1Icon1 from '../assets/ai1.png';

// Lazy load tab components
const GeneralInfoTab = lazy(() => import('./tabs/GeneralInfoTab'));
const AISettingsTab = lazy(() => import('./tabs/AISettingsTab'));
const KnowledgeBaseTab = lazy(() => import('./tabs/KnowledgeBaseTab'));
const ChatsTab = lazy(() => import('./tabs/ChatsTab'));
const LeadsTab = lazy(() => import('./tabs/LeadsTab'));
const SubscriptionTab = lazy(() => import('./tabs/SubscriptionTab'));
const AiConsultantTab = lazy(() => import('./tabs/AiConsultantTab'));
 

const tabs = [
  { titleKey: 'tabs.general', key: 'general', icon: [generalIcon, generalIcon1] },
  { titleKey: 'tabs.bot', key: 'bot', icon: [aiIcon, ai1Icon1] },
  { titleKey: 'tabs.ai', key: 'ai', icon: [settingsIcon, settingsIcon1] },
  { titleKey: 'tabs.kb', key: 'kb', icon: [kbIcon, kbIcon1] },
  { titleKey: 'tabs.chats', key: 'chats', icon: [chatsIcon, chatsIcon1] },
  { titleKey: 'tabs.leads', key: 'leads', icon: [leadsIcon, leadsIcon1] },
  { titleKey: 'tabs.sub', key: 'sub', icon: [subscribeIcon, subscribeIcon1] },
];

const CabinetPageInner = () => {
  const { tab, conversationId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [openConversationId, setOpenConversationId] = useState(null);
  const tabContentRef = useRef(null);
  const { query } = useContext(SearchContext);

  // =====================================================
  // Translation
  // =====================================================
  const { t } = useTranslation("cabinetpage");

  // Динамически переводим массив вкладок для передачи в Sidebar
  const translatedTabs = tabs.map(tabItem => ({
    ...tabItem,
    label: t(tabItem.titleKey)
  }));

  // Инициализация активной вкладки из URL при загрузке и при изменении параметра
  useEffect(() => {
    if (tab) {
      const tabIndex = tabs.findIndex(t => t.key === tab);
      if (tabIndex !== -1) {
        setActiveTab(tabIndex);
      } else {
        // Если вкладка не найдена, редирект на первую
        navigate('/cabinet/general', { replace: true });
      }
    }
  }, [tab, navigate]);

  // Обработка conversationId из URL для вкладки chats
  useEffect(() => {
    if (tab === 'chats' && conversationId) {
      setOpenConversationId(conversationId);
      // Сбрасываем через небольшую задержку, чтобы компонент Chats успел его обработать
      setTimeout(() => setOpenConversationId(null), 500);
    }
  }, [tab, conversationId]);

  // Обновление URL при смене вкладки
  const handleTabClick = useCallback((index) => {
    const tabKey = tabs[index]?.key;
    if (tabKey) {
      navigate(`/cabinet/${tabKey}`);
    }
  }, [navigate]);

  const handleOpenConversation = useCallback((conversationId) => {
    if (!conversationId) return;
    // Переход на вкладку чатов с указанным conversationId
    navigate(`/cabinet/chats/${conversationId}`);
  }, [navigate]);

  // mark.js — подсветка текущей вкладки (теперь реагируем на query из контекста,
  // т.к. SearchHeader делает дебаунс сам)
  useEffect(() => {
    const ctx = tabContentRef.current;
    if (!ctx) return;
    const instance = new Mark(ctx);

    instance.unmark({
      done: () => {
        const term = query && query.trim();
        if (term) {
          instance.mark(term, {
            separateWordSearch: false,
            acrossElements: true,
            element: 'mark',
            exclude: ['[data-no-mark="true"]'],
          });
        }
      }
    });

    return () => {
      try { instance.unmark(); } catch (e) {}
    };
  }, [query, activeTab]);

  return (
    <div className="cabinet-container">
      <Sidebar tabs={translatedTabs} activeTab={activeTab} onTabClick={handleTabClick} />
      <main className="tab-content">
        <SearchHeader title={tabs[activeTab] ? t(tabs[activeTab].titleKey) : ''} />
        <div ref={tabContentRef} className="tab-viewport">
          <Suspense fallback={<div className="loading-tab">{t("loading")}...</div>}>
            {activeTab === 0 && <GeneralInfoTab />}
            {activeTab === 1 && <AiConsultantTab />}
            {activeTab === 2 && <AISettingsTab />}
            {activeTab === 3 && <KnowledgeBaseTab />}
            {activeTab === 4 && <ChatsTab openConversationId={openConversationId} />}
            {activeTab === 5 && <LeadsTab onOpenConversation={handleOpenConversation} />}
            {activeTab === 6 && <SubscriptionTab />}
          </Suspense>
        </div>
      </main>
    </div>
  );
};

const CabinetPage = () => (
  <SearchProvider>
    <CabinetPageInner />
  </SearchProvider>
);

export default CabinetPage;